<template>
    <v-card width="100%" class="ma-0 pa-0" outlined>
        <v-overlay v-if="loading">
            <v-progress-circular indeterminate color="primary"/>
        </v-overlay>
        <v-data-table
            style="width: 100%;"
            fixed-header
            :items="configs"
            :headers="headers"
            hide-default-footer
            disable-pagination>
            <template v-slot:item.group="{ item }">
                <v-btn outlined
                       @click="onPressEditGroup(item)"
                       small
                       class="orange--text subtitle-2">
                    {{ item.group }}
                </v-btn>
            </template>
            <template v-slot:item.meta.uid="{ item }">
                <v-card-text class="blue--text subtitle-2 ma-0 pa-0">{{ getOwner(item) }}</v-card-text>
            </template>
            <template v-slot:item.meta.updated="{ item }">
                {{ getUpdated(item) | formatDate }}
            </template>
            <template v-slot:item.actions="{ item }">
                <v-tooltip bottom max-width="50%">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon
                               color="orange"
                               v-bind="attrs"
                               v-on="on"
                               class="ma-0 pa-0"
                               @click="onPressInfo(item)">
                            <v-icon size="22">mdi-information</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ item.info?.length > 0 ? item.info : "no info" }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon
                               color="green"
                               v-bind="attrs"
                               v-on="on"
                               class="ma-0 pa-0"
                               @click="onPressEdit(item)">
                            <v-icon size="20">mdi-pencil</v-icon>
                        </v-btn>
                    </template>
                    <span>Edit</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon
                               color="teal lighten-1"
                               v-bind="attrs"
                               v-on="on"
                               class="ma-0 pa-0"
                               @click="onPressDuplicate(item)">
                            <v-icon size="22">mdi-content-copy</v-icon>
                        </v-btn>
                    </template>
                    <span>Duplicate</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon
                               color="red"
                               v-bind="attrs"
                               v-on="on"
                               class="ma-0 pa-0"
                               @click="onPressDelete(item)">
                            <v-icon size="20">mdi-delete</v-icon>
                        </v-btn>
                    </template>
                    <span>Delete</span>
                </v-tooltip>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
import {ConfigStoreUtils} from "@/modules/mconfigs/database/ConfigStoreUtils";
import {ConfigModel} from "@/modules/mconfigs/models/ConfigModel";
import {PermissionsStoreUtils} from "@/modules/mpermissions/database/PermissionsStoreUtils";

export default {
    name: "c-shared-configs-table",
    data: () => ({
        headers: [
            {text: 'Name', sortable: true, value: 'group', class: "white blue--text"},
            {text: 'Id', sortable: true, value: 'uid', class: "white blue--text"},
            {text: 'Owner', sortable: true, value: 'meta.uid', class: "white blue--text"},
            {text: 'Updated', sortable: true, value: 'meta.updated', class: "white blue--text"},
            {text: 'Actions', sortable: false, value: 'actions', width: '180px', class: "white blue--text"}
        ],
        loading: false,
    }),
    methods: {
        getOwner(config) {
            if (config.meta == null) return "no metadata";
            return PermissionsStoreUtils.getUserName(config.meta.uid);
        },
        getUpdated(config) {
            if (config.meta == null) return "no metadata";
            return config.meta.updated;
        },
        onPressInfo(item) {
            this.$emit('show-edit-info-popup', item);
        },
        onPressEditGroup(item) {
            this.$emit('show-edit-group-popup', item);
        },
        onPressEdit(item) {
            this.$router.push({
                name: "m-redactor",
                params: {uid: item.uid, path: "configs_data/", readonly: false, name: item.group},
                query: {shared: '1'}
            });
        },
        onPressDuplicate(item) {
            this.loading = true;
            ConfigStoreUtils.getConfigData("configs_data/" + item.uid)
                .then((data) => {
                    let config = new ConfigModel(item.group + "_copy", 0);
                    config.info = item.info;
                    ConfigStoreUtils.pushSharedConfig(config, data)
                        .then(() => {
                            this.$notify({title: 'Shared config duplicated!', type: "success"});
                        }).catch((error) => {
                            this.$notify({title: 'Duplicate failed!', text: error, type: "error"});
                        }).finally(() => {
                            this.loading = false;
                        });
                }).catch((error) => {
                    this.$notify({title: 'Failed to get config data!', text: error, type: "error"});
                    this.loading = false;
                });
        },
        onPressDelete(item) {
            this.loading = true;
            ConfigStoreUtils.findLinkedConfigs(item.uid).then((linked) => {
                this.loading = false;
                let message = "Are you sure you want to delete this shared config?";
                if (linked.length > 0) {
                    let names = linked.map(c => c.name).join(", ");
                    message += "\n\nThe following configs reference it and will be unlinked:\n" + names;
                }
                this.$confirm(message).then(() => {
                    this.loading = true;
                    ConfigStoreUtils.unlinkSharedConfigs(linked)
                        .then(() => ConfigStoreUtils.deleteSharedConfig(item.uid))
                        .then(() => {
                            this.$notify({title: 'Shared config deleted!', type: "success"});
                        }).catch((error) => {
                            this.$notify({title: 'Delete failed!', text: error, type: "error"});
                        }).finally(() => {
                            this.loading = false;
                        });
                }).catch(() => {});
            }).catch(() => {
                this.loading = false;
                this.$notify({title: 'Failed to check linked configs', type: "error"});
            });
        }
    },
    computed: {
        configs() {
            return ConfigStoreUtils.getSharedConfigs();
        }
    }
}
</script>
